import React from 'react';
import { css } from '@emotion/react';
import { IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, contextFactory, RadioFieldLayout, Text, useFormField, useRadioContext } from '@frontend/design-system';
import { generateTrackingId } from '../utils';

type ContentSwitcherCtx = {
  onValueChange: (nextValue: string) => void;
};

const [ContentSwitcherCtx, useContentSwitcherContext] = contextFactory<ContentSwitcherCtx>(
  'ContentSwitcher must wrap its Options'
);

type Props<T extends string> = {
  children: Array<React.ReactElement | null>;
  defaultValue: T;
  onValueChange: (nextValue: T) => void;
};

export const ContentSwitcher = <T extends string>({ children, defaultValue, onValueChange }: Props<T>) => {
  const contentSwitcherFieldProps = useFormField({
    type: 'optionswitch',
    value: defaultValue,
  });

  return (
    <ContentSwitcherCtx.Provider value={{ onValueChange: (next: string) => onValueChange(next as T) }}>
      <RadioFieldLayout {...contentSwitcherFieldProps} name='contentSwitcher'>
        <div css={styles.container}>{children}</div>
      </RadioFieldLayout>
    </ContentSwitcherCtx.Provider>
  );
};

type ContentSwitchOptionProps<T extends string> = {
  value: T;
  iconName?: IconName;
  label?: string;
  disabled?: boolean;
  trackingId?: string;
  children?: React.ReactNode;
};

export const ContentSwitchOption = <T extends string>({
  value,
  iconName,
  label,
  trackingId,
}: ContentSwitchOptionProps<T>) => {
  const { getOptionProps } = useRadioContext();
  const { onValueChange } = useContentSwitcherContext();

  const { active, checked, name, onChange, ...optProps } = getOptionProps(value);

  return (
    <Button
      css={styles.optionButton(checked)}
      {...optProps}
      iconName={iconName}
      onClick={() => {
        onChange({ name, value });
        onValueChange(value);
      }}
      type='button'
      trackingId={generateTrackingId({ component: 'content-switcher', context: trackingId ?? value })}
    >
      {label && (
        <Text className='content-switcher-label' size='medium'>
          {label}
        </Text>
      )}
    </Button>
  );
};

ContentSwitcher.Option = ContentSwitchOption;

const styles = {
  container: css`
    display: flex;
    align-items: center;
    height: ${theme.spacing(4)};
    background-color: ${theme.colors.neutral10};
    border-radius: ${theme.spacing(0.75)};
    padding: ${theme.spacing(0.25)};
  `,
  optionButton: (checked: boolean) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    padding: ${theme.spacing(0.5, 1)};
    border-radius: ${theme.spacing(0.5)};
    background: ${checked ? theme.colors.white : 'transparent'};
    box-shadow: ${checked ? theme.shadows.light : 'none'};
    height: ${theme.spacing(3.5)};

    svg {
      color: ${theme.font.colors.subdued};
      height: ${theme.spacing(2)};
      min-height: ${theme.spacing(2)};
      width: ${theme.spacing(2)};
      min-width: ${theme.spacing(2)};
    }

    .content-switcher-label {
      font-weight: ${checked ? theme.font.weight.bold : theme.font.weight.regular};
      color: ${checked ? theme.font.colors.default : theme.font.colors.subdued};
    }

    &:hover {
      background-color: ${checked ? theme.colors.white : theme.colors.neutral20};
    }

    &:active {
      background: ${checked ? theme.colors.white : 'transparent'} !important;
      color: ${theme.font.colors.default} !important;

      svg {
        color: ${theme.font.colors.subdued} !important;
      }
    }

    &:focus-visible {
      box-shadow: ${theme.shadows.light};
    }
  `,
};
